import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';

import makeArray from 'webapp/helpers/make-array';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckContainer, {
	containerEventName,
} from 'webapp/modifiers/visible-check-container';
import MetricLibraryGridItemComponent from 'webapp/components/metric-library/grid-item';
import MetricLibraryGridComponent from 'webapp/components/metric-library/grid/index';

import type { Metric } from 'webapp/models-custom';
import BatchSelect from 'webapp/models-custom/batch-select';
import FilterSortMetricLibraryMainMetrics from 'webapp/models-custom/filter-sort/metric-library-main-metrics';

interface Args {
	batchSelect: BatchSelect<Metric>;
	metricsByPopularity: Metric[];
}

export default class MetricLibraryPopularMetricsComponent extends Component<Args> {
	@service('account') declare accountService: Service.Account;
	@service('flow/tour/onboarding')
	declare flowTourOnboardingService: Service.Flow.Tour.Onboarding;
	@service('model-manager/sources') declare mmSourcesService: Service.ModelManager.Sources;

	@tracked maxItems = 1;
	filterSort;
	tourBatchSelect = new BatchSelect<Metric>({
		getAllItems: () => [],
		getOptions: () => [],
		doBatchAction: () => Promise.resolve(),
	});

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.filterSort = new FilterSortMetricLibraryMainMetrics({
			getAllItems: () => {
				return this.metrics;
			},
		});

		this.filterSort.sortProp = ''; // To prevent sortering on the initial load (because of metrics from first connected source)
	}

	@cached
	get metrics() {
		const cloudSourcesOpportunity = this.accountService.metadata.cloudSourcesOpportunity,
			firstConnectedSourceTypeKey = this.mmSourcesService.firstConnectedCloudSourceType?.key,
			metrics = this.args.metricsByPopularity;

		let fromFirstConnectedSourceCounter = 0;
		let filteredMetrics: Metric[] = [];
		if (this.flowTourOnboardingService.isBannerShown) {
			filteredMetrics = this.flowTourOnboardingService.filteredPopularMetrics;
		} else {
			filteredMetrics = metrics.filter((metric: Metric) => {
				const isFromFirstConnectedSource =
					metric.dataSourceTypeKey === firstConnectedSourceTypeKey;

				if (isFromFirstConnectedSource) {
					fromFirstConnectedSourceCounter++; // The first 9 metrics are from the first connected cloud source type
				}

				return (
					(isFromFirstConnectedSource && fromFirstConnectedSourceCounter < 10) ||
					![firstConnectedSourceTypeKey, 'GoogleAnalytics'].includes(
						metric.dataSourceTypeKey,
					)
				);
			});
		}

		filteredMetrics = filteredMetrics.filter((metric: Metric) => !metric.isDeprecated);

		return filteredMetrics
			.sort((firstMetric: Metric, secondMetric: Metric) => {
				const isFirstMetricFromFirstSource =
						firstMetric.dataSourceTypeKey === firstConnectedSourceTypeKey,
					isSecondMetricFromFirstSource =
						secondMetric.dataSourceTypeKey === firstConnectedSourceTypeKey;

				if (isFirstMetricFromFirstSource && !isSecondMetricFromFirstSource) {
					return -1;
				} else if (!isFirstMetricFromFirstSource && isSecondMetricFromFirstSource) {
					return 1;
				}

				const isFirstMetricFromOpportunitySource = cloudSourcesOpportunity.includes(
						firstMetric.dataSourceTypeKey,
					),
					isSecondMetricFromOpportunitySource = cloudSourcesOpportunity.includes(
						secondMetric.dataSourceTypeKey,
					);
				if (isFirstMetricFromOpportunitySource && !isSecondMetricFromOpportunitySource) {
					return -1;
				} else if (
					!isFirstMetricFromOpportunitySource &&
					isSecondMetricFromOpportunitySource
				) {
					return 1;
				} else {
					return 0;
				}
			})
			.slice(0, this.maxItems);
	}

	// The guided tour only counts selections; nothing is saved.
	@action
	selectForTour(model: Metric) {
		this.flowTourOnboardingService.selectMetrics(model.id!);
	}

	@action
	updateMaxDisplayedItems(n: number): void {
		this.maxItems = Math.max(Math.ceil(15 / n) * n, n * 3);
	}

	@action
	triggerContentChange(node: HTMLElement): void {
		// for visible-check
		node.dispatchEvent(new Event(containerEventName));
	}
	<template>
		<MetricLibraryGridComponent
			@filterSort={{this.filterSort}}
			@title="Popular metrics"
			@classNames="popular-metrics"
			@onItemsPerRowCalculated={{this.updateMaxDisplayedItems}}
			@animationProperty="uniqueId"
			@hideCountBadge={{true}}
			as |visibleItems settings|
		>
			<ul
				class="flex-ct flex-ct--size-block-metric"
				{{visibleCheckContainer}}
				{{didUpdate this.triggerContentChange visibleItems}}
			>
				{{#each visibleItems as |model|}}
					{{#if this.flowTourOnboardingService.isBannerShown}}
						<MetricLibraryGridItemComponent
							@model={{model}}
							@canSelect={{true}}
							@hideActions={{true}}
							@batchSelect={{this.tourBatchSelect}}
							@onSelectMetric={{this.selectForTour}}
						/>
					{{else}}
						<MetricLibraryGridItemComponent
							@model={{model}}
							@canSelect={{model.isOnSource}}
							@batchSelect={{@batchSelect}}
						/>
					{{/if}}
				{{/each}}

				{{! adding (max-in-line - 1) fake items so the incomplete last row items are not stretched too much }}
				{{#each (makeArray length=settings.itemsPerRow)}}
					<li class="flex-item flex-item--fake"></li>
				{{/each}}
			</ul>
		</MetricLibraryGridComponent>
	</template>
}
